import React, { useEffect, useState, useRef } from 'react';

import _, { pick } from 'lodash';
import shortid from 'shortid';
import cx from 'classnames';
import useIsMounted from 'components/Editor/hooks/useEditorSession';
import Draggable from 'react-draggable';

import Portal from 'components/Portal';

import Select from 'react-select';
import { colorToString } from 'components/Editor/helper/color';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faListUl, faSlash, faSquare, faLock, faUnlock } from '@fortawesome/free-solid-svg-icons';

import { Form, OverlayTrigger, Popover, Accordion, AccordionContext, Button, Overlay, FormCheck } from 'react-bootstrap';
import CustomPicker from 'components/ColorPicker';
import Tooltip from 'components/Tooltip';

import styles from './properties.module.scss';
import { getColorsConfig } from 'services/cssHelper.service';

import { FREE, PAGE, STRETCHED } from 'constants/widgetDisplayTypes';

const Property = (props) => {
  return (
    <div className={cx(styles.property, props.className)}>
      {props.name &&
        <div className={cx(styles.name, props.nameStyle)}>{props.name}</div>
      }
      <div className={styles.control}>{props.children}</div>
    </div>
  );
}

const PropertyDialog = React.forwardRef((props, ref) => {
  const idRef = React.useRef(`dialog_${shortid()}`);
  const defaultRef = React.useRef(null);
  const dialogRef = ref || defaultRef;

  const [position, setPosition] = useState(null);

  const dialogStyle = {
    width: props.width,
    height: props.height,
    visibility: position != null ? 'visible' : 'hidden'
  }

  const calculateDialogPosition = () => {
    const target = props.target;
    var targetEl = typeof target === 'string' ? document.querySelector(target) : target;

    if (!targetEl) {
      console.warn(`Target ${target} not found`);
      return;
    }

    if (!dialogRef.current) {
      console.warn(`Dialog ref unmounted`);
      return;
    }

    const width = props.width;
    const height = dialogRef.current.getBoundingClientRect().height;

    const rect = targetEl.getBoundingClientRect();

    const minLeft = 30;
    const maxleft = Math.max(window.innerWidth - rect.width - 30, minLeft);

    const minTop = 30;
    const maxTop = Math.max(window.innerHeight - height - 20, minTop);

    var left = rect.left - width - 20;
    if (width > rect.left - 30) {
      left = rect.left + rect.width + 20;
    }

    left = Math.max(minLeft, left);
    left = Math.min(maxleft, left);

    var top = rect.top;
    top = Math.max(minTop, top);
    top = Math.min(maxTop, top)

    setPosition({
      x: left,
      y: top
    })
  }

  const handlePositionChange = (e, ui) => {
    setPosition((position) => {
      return {
        x: position.x + ui.deltaX,
        y: position.y + + ui.deltaY
      };
    });
  }

  const attachResizeObserver = () => {
    if (!dialogRef.current) {
      console.warn(`Target not found`);
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      calculateDialogPosition();
    });

    resizeObserver.observe(dialogRef.current);
  }

  useEffect(() => {
    attachResizeObserver();
    calculateDialogPosition();
  }, []);


  return (
    <Portal>
      {props.modal &&
        <div className={cx(styles.overlay, props.overlayClassName)} onClick={props.onCancel}></div>
      }
      <Draggable handle={`#${idRef.current} .${styles.dragHandle}`} bounds='parent' position={position} onDrag={handlePositionChange}>
        <div id={idRef.current} ref={dialogRef} className={cx(styles.dialog, props.className)} style={dialogStyle}>
          {props.title &&
            <Property.Header title={props.title} onBack={props.onBack} onCancel={props.onCancel} />
          }
          {props.children}
        </div>
      </Draggable>
    </Portal>
  );
});

Property.Dialog = PropertyDialog;

const PropertyHeader = (props) => {
  return (
    <header className={styles.dragHandle}>

      <div className={styles.actions}>
        {props.onBack &&
          <Button className={cx(styles.btnAction, styles.back)} onClick={props.onBack}>
            <i className='cleverstory-left' />
          </Button>
        }
        <div className={cx(styles.title, props.className)}>
          {props.title}
        </div>
      </div>

      <i className={cx(styles.draggabel, 'cleverstory-drag')} />
      <div className={styles.actions}>
        {props.children}

        {props.onCancel &&
          <Button className={cx(styles.btnAction, styles.close)} onClick={props.onCancel}>
            <i className='cleverstory-close' />
          </Button>
        }
      </div>
    </header>
  );
}

Property.Header = PropertyHeader;

const PropertyBody = (props) => {
  return (
    <div className={cx(styles.body, props.className)}>
      {props.children}
    </div>
  );
}

Property.Body = PropertyBody;


const PropertyDropDown = React.forwardRef(({ className, options, multiple, disabled=false, searchable, placeholder, components, value, error, onChange, input, inputChange, ...rest }, ref) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  }

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#EEEEEE' : '#ffffff',
    }),
  }

  const selected = options.filter(option => {
    return option.value === value;
  });

  return (
    <div className={styles.dropdownMenuContainer}>
      <Select
        className={cx(styles.dropdown, className)}
        classNamePrefix={"dropdown"}
        styles={customStyles}
        isDisabled={disabled}
        options={options}
        isMulti={multiple}
        isSearchable={searchable}
        placeholder={placeholder}
        components={components}
        menuPortalTarget={document.body}
        value={selected}
        onChange={handleChange}
        inputValue={input}
        onInputChange={inputChange}
        ref={ref}
        {...rest}
      />
      <p className={styles.error}>{error}</p>
    </div>
  );
})

function propertyDialogEqual(prevProps, nextProps) {
  const paths = ['value', 'options', 'multiple', 'searchable', 'placeholder', 'className', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.Dropdown = React.memo(PropertyDropDown, propertyDialogEqual);

const PropertyInput = (props) => {
  return (
    <div className={cx(styles.inputContainer, props.className)}>
      <input className={styles.input}
        value={props.value}
        onChange={props.onChange}
        onBlur={props.onBlur && props.onBlur}
        placeholder={props.placeholder}
        type={props.type || ''}
        disabled={props.disabled}
      />
      <p className={styles.error}>{props.error}</p>
    </div>
  )
}

Property.Input = PropertyInput;

const PropertyNumber = (props) => {
  const [input, setInput] = useState(props.value);

  useEffect(() => {
    setInput(props.value);
  }, [props.value]);

  const handleChange = (value) => {
    if (isNaN(value)) {
      return;
    }

    if (props.onChange) {
      props.onChange(value);
    }
  }

  const handleTextChange = (e) => {
    setInput(e.target.value)
  }

  const handleTextBlur = (e) => {
    var value = e.target.value;
    if (isNaN(value)) {
      value = props.value;
    }

    if (props.min || props.min === 0) {
      value = Math.max(props.min, value);
    }

    if (props.max) {
      value = Math.min(props.max, value);
    }

    handleChange(value);
    setInput(value);
  }

  const processChange = (value) => {
    if (props.min || props.min === 0) {
      value = Math.max(props.min, value);
    }

    if (props.max) {
      value = Math.min(props.max, value);
    }
    handleChange(value);
    setInput(value);
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processChange(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var newValue = parseInt(input) + 1;
      newValue = Math.min(props.max, newValue);
      processChange(newValue);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      var newValue = parseInt(input) - 1;
      newValue = Math.max(props.min, newValue);
      processChange(newValue);
    }
  }

  return (
    <div className={cx(styles.inputContainer, props.className)}>
      <input className={styles.input}
        value={input}
        placeholder={props.placeholder}
        type={props.type || ''}
        disabled={props.disabled}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onBlur={handleTextBlur}
      />
      <p className={styles.error}>{props.error}</p>
    </div>
  )
}

Property.Number = PropertyNumber;

const PropertySlider = (props) => {
  const [input, setInput] = useState(props.value);
  const mounted = useIsMounted();

  useEffect(() => {
    return () => {
      if (mounted()) {
        return;
      }

      if (isNaN(input)) {
        return;
      }


      if (input != props.value) {
        processChange(input);
      }
    }
  }, [mounted]);

  useEffect(() => {
    if (isNaN(props.value)) {
      return;
    }

    setInput(props.value);
  }, [props.value]);

  const handleChange = (value) => {
    if (isNaN(value)) {
      return;
    }

    if (props.onChange) {
      props.onChange(value);
    }
  }

  const handleSliderChange = (e) => {
    handleChange(e.target.value);
  }

  const handleTextChange = (e) => {
    setInput(e.target.value)
  }

  const handleTextBlur = (e) => {
    var value = e.target.value;
    if (isNaN(value)) {
      value = props.value;
    }

    if (props.min || props.min === 0) {
      value = Math.max(props.min, value);
    }

    if (props.max) {
      value = Math.min(props.max, value);
    }

    handleChange(value);
    setInput(value);
  }

  const processChange = (value) => {
    if (props.min || props.min === 0) {
      value = Math.max(props.min, value);
    }

    if (props.max) {
      value = Math.min(props.max, value);
    }
    handleChange(value);
    setInput(value);
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processChange(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var newValue = parseInt(input) + 1;
      newValue = Math.min(props.max, newValue);
      processChange(newValue);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      var newValue = parseInt(input) - 1;
      newValue = Math.max(props.min, newValue);
      processChange(newValue);
    }
  }

  return (
    <div className={cx(styles.slider, props.className)}>
      <Form.Range
        className={styles.sliderControl}
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onChange={handleSliderChange}
      />

      {props.input &&
        <input className={styles.value}
          type='text'
          value={input}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onBlur={handleTextBlur}
        />
      }
    </div>
  );
}

function propertySliderEqual(prevProps, nextProps) {
  const paths = ['value', 'min', 'max', 'step', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.Slider = React.memo(PropertySlider, propertySliderEqual);

const PropertySlideChanger = (props) => {
  const {slides, currentSlideId, onChange} = props;

  const currentOrder = _.find(slides, { uid: currentSlideId });
  const [currentSlide, setCurrentSlide] = useState(0);

  const goToPrevious = () => {
    const prevIndex = currentSlide === 0 ? slides.length - 1 : (currentSlide - 1)
    onChange((prevIndex + 1));
    setCurrentSlide(prevIndex);
  };

  useEffect(()=>{
    if(currentOrder) {
      setCurrentSlide((_.get(currentOrder, 'order') - 1))
    }
  },[currentOrder])

  const goToNext = () => {
    const nextIndex = currentSlide === slides.length - 1 ? 0 : (currentSlide + 1)
    onChange((nextIndex + 1));
    setCurrentSlide(nextIndex);
  };

  useEffect(()=>{
    if((currentSlide + 1) > slides.length ) {
      setCurrentSlide((_.size(slides) - 1));
    }
  },[slides])

  return (
    <div className={styles.sliderControl}>
      <Button className={styles.leftButton} variant='info' onClick={()=>goToPrevious()}>
        <i className={cx('cleverstory-drop-down', styles.leftSide)} />
      </Button>
      <span className={styles.counterText}>{`${currentSlide + 1} / ${slides.length}`}</span>
      <Button className={styles.rightButton} variant='info' onClick={()=>goToNext()}>
        <i className={cx('cleverstory-drop-down', styles.rightSide)} />
      </Button>
    </div>
  )
}

function propertySlideChangerEqual(prevProps, nextProps) {
  const paths = ['slides', 'onChange', 'parent', 'currentSlideId'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.SlideChanger = React.memo(PropertySlideChanger, propertySlideChangerEqual);

const PropertyRotation = (props) => {

  const [input, setInput] = useState(props.value);
  const targetRef = useRef(null);

  useEffect(() => {
    setInput(props.value);
    targetRef.current.style.transform = `rotate(${props.value}deg)`;
  }, [props.value]);

  const handleChange = (value) => {
    if (isNaN(value)) {
      return;
    }

    if (props.onChange) {
      props.onChange(value);
    }
  }

  const handleTextChange = (e) => {
    setInput(e.target.value);
  }

  const handleTextBlur = (e) => {
    var value = e.target.value;
    if (isNaN(value)) {
      value = props.value;
    }

    if (props.min) {
      value = Math.max(props.min, value);
    }

    if (props.max) {
      value = Math.min(props.max, value);
    }

    handleChange(value);
    setInput(value);
  }

  const processChange = (value) => {
    if (props.min) {
      value = Math.max(props.min, value);
    }

    if (props.max) {
      value = Math.min(props.max, value);
    }

    handleChange(value);
  }

  const calculateAngle = (eventData) => {
    var targetDimension = targetRef.current.getBoundingClientRect();

    const x1 = targetDimension.left + (targetDimension.width / 2);
    const y1 = targetDimension.top + (targetDimension.height / 2);
    const deltaX = x1 - eventData.clientX;
    const deltaY = y1 - eventData.clientY;

    const rad = Math.atan2(deltaY, deltaX);

    let deg = (rad * (180 / Math.PI)) - 90;

    if (deg < 0) {
      deg += 360;
    }

    setInput(Math.round(deg))
    props.onChange(Math.round(deg));

    return deg;
  }


  useEffect(() => {
    const handleRotate = event => {
      const result = Math.floor(calculateAngle(event));
      targetRef.current.style.transform = `rotate(${result}deg)`;
    };

    const element = targetRef.current;

    element.addEventListener('mousedown', () => {
      element.addEventListener('mousemove', handleRotate);

      element.addEventListener('mouseup', () => {
        element.removeEventListener('mousemove', handleRotate)
      });
    });

    return () => {
      element.removeEventListener('mousedown', handleRotate);
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processChange(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var newValue = parseInt(input) + 1;
      newValue = Math.min(props.max, newValue);
      processChange(newValue);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      var newValue = parseInt(input) - 1;
      newValue = Math.max(props.min, newValue);
      processChange(newValue);
    }
  }

  return (
    <div className={cx(styles.rotationControlWrapper, styles.className)}>
      <div>
        <div ref={targetRef} className={styles.circularDial}>
          <div className={styles.circleCenter}>
            <div className={styles.knob} />
          </div>
        </div>
      </div>

      {props.input &&
        <input className={styles.rotationValue}
          type='text'
          value={Math.round(input)}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onBlur={handleTextBlur}
        />
      }
    </div>
  );
}

function propertyRotationEqual(prevProps, nextProps) {
  const paths = ['value', 'min', 'max', 'step', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.Rotation = React.memo(PropertyRotation, propertyRotationEqual);

const PropertyToggle = React.forwardRef((props, ref) => {
  const handleClick = () => {
    if (props.onChange) {
      props.onChange(!props.active);
    }
  }

  return (
    <button ref={ref} className={cx(styles.toggleButton, props.className, {
      [styles.active]: props.active
    })} onClick={handleClick}>
      <FontAwesomeIcon icon={props.icon} />
    </button>
  );
})

function propertyToggleEqual(prevProps, nextProps) {
  const paths = ['active', 'className', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.Toggle = React.memo(PropertyToggle, propertyToggleEqual);

const PropertyColorButton = (props) => {
  const targetRef = React.useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showPopover, setShowPopover] = useState(false);

  var value =  props.value || 'transparent';
  const pickerColor = value === 'transparent' ? { r: 255, g: 255, b: 255, a: 1 } : value;

  var colorConfig = {};
  if (props.presetColors) {
    colorConfig['presetColors'] = props.presetColors;
  }

  const handleColorChange = (color) => {
    if (props.onChange) {
      props.onChange(color);
    }

    document.body.click();
  }

  const handleTargetClicked = () => {
    if (props.clearable && props.value != null) {
      setShowPopover(true);
      setShowColorPicker(false);
    } else {
      setShowPopover(false);
      setShowColorPicker(true);
    }
  }

  return (
    <>
      {React.cloneElement(props.trigger, {
        ref: targetRef,
        onClick: handleTargetClicked
      })}
      <Overlay
        show={showPopover}
        target={targetRef.current}
        placement="bottom"
        rootClose
        onHide={() => {
          setShowPopover(false);
        }}
      >
        <Popover className={styles.colorDialog}>
          <Popover.Body>
            <div className={styles.options}>
              <button className={styles.option} onClick={() => {
                handleColorChange({ r: 255, g: 255, b: 255, a: 0 });
              }}>
                <i className='cleverstory-none' />
              </button>
              <button className={styles.option} onClick={() => {
                setShowColorPicker(true);
                setShowPopover(false);
              }}>
                <span className={styles.color} style={{
                  background: getColorsConfig(pickerColor)
                }} />
              </button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
      {showColorPicker &&
        <ColorPickerDialog
          target={targetRef.current}
          colorConfig={props.value}
          gradient={props.gradient}
          value={pickerColor}
          onChange={handleColorChange}
          onCancel={() => {
            setShowPopover(false);
            setShowColorPicker(false);
          }}
        />
      }

    </>
  )
}

function propertyColorButtonEqual(prevProps, nextProps) {
  const paths = ['value', 'presetColors', 'gradient', 'clearable', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.ColorButton = React.memo(PropertyColorButton, propertyColorButtonEqual);

/**
 * Color Property
 * 
*/
const PropertyColor = ({ title, className, value, gradient = false, clearable, onChange }) => {

  // value = value.color || value || 'transparent';
  const pickerColor = getColorsConfig(value);

  return (
    <div className={cx(styles.colorStyle, className)}>
      <label className={styles.title}>{title}</label>
      <Property.ColorButton
        trigger={
          <button className={styles.button}
            style={{
              background: pickerColor,
            }}
          />
        }
        gradient={gradient}
        clearable={clearable}
        value={value}
        onChange={onChange}
      />
    </div>
  )
}

function propertyColorEqual(prevProps, nextProps) {
  const paths = ['title', 'value',  'gradient', 'clearable', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.Color = React.memo(PropertyColor, propertyColorEqual);


/**
 * Color Picker
 */
function propertyColorDialogEqual(prevProps, nextProps) {
  const paths = ['target', 'colorConfig', 'gradient', 'value', 'onChange', 'onCancel'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const ColorPickerDialog = React.memo(({ target, colorConfig, gradient, value, onChange, onCancel }) => {
  const dialogRef = React.useRef(null);

  const [state, setState] = useState({
    colorConfig: {},
    color: value,
  })
  
  useEffect(() => {
    const colorType = _.get(value, 'type', '');
    const pickerColor = getColorsConfig((value.r == null || value.g == null || value.b == null || value.a == null ) ? {r:0, g:0, b:0, a:1} : value);

    setState(state => {
      return {
        ...state,
        color: pickerColor,
      }
    })
  }, []);

  const handleColorChange = (color) => {
    setState(state => {
      return {
        ...state,
        colorConfig: color,
        color: color.rgb
      }
    })
  };

  const handleConfirm = (gradientConfig) => {
    if(gradientConfig) {
      applyColorChange(gradientConfig);
    } 
    else {
      applyColorChange(state.colorConfig)
    } 
    onCancel()
  }

  const applyColorChange = _.debounce((color) => {
    let request = {
      ...color.rgb,
      type: 'solid' 
    }
    
    if(color.type === 'gradient') {
      request = {
        type: color.type,
        gradientType: color.gradientType,
        degree: color.degree,
        gradientColors: color.gradientColors
      }
    }
    onChange(request);
  }, 300);

  return (
    <Property.Dialog ref={dialogRef}
      className={styles.colorPickerDialog}
      overlayClassName={styles.colorDialogOverlay}
      width={220}
      target={target}
      title={'Color Picker'}
      modal
      onCancel={onCancel}
    >
      <Property.Body>
        <CustomPicker
          {...colorConfig}
          color={state.color}
          colorConfig={state.colorConfig}
          onConfirm={handleConfirm}
          gradient={gradient}
          onClose={onCancel}
          onChange={handleColorChange}
        />
      </Property.Body>
    </Property.Dialog>
  );
}, propertyColorDialogEqual);

Property.ColorPickerDialog = ColorPickerDialog;

/**
 * Wdget Actions
 */
function Action({ icon, tooltip, children, onClick, ...rest }) {
  const action = (
    <button className={cx(styles.action, rest.className)} onClick={(e) => {
      if (onClick) {
        onClick(e);
      }
    }}>
      {children}
    </button>
  );

  if (tooltip) {
    return (
      <Tooltip placement={'top'} content={tooltip}>
        {action}
      </Tooltip>
    );
  }

  return action;
}

Property.Action = Action;

function Divider({ icon, tooltip, children, onClick, ...rest }) {
  return (
    <div className={cx(styles.divider, rest.className)}></div>
  );
}

Property.Divider = Divider;


const PropertyRadioButton = ({ title, value, onClick, className }) => {
  return (
    <div className={cx(styles.radioProperty, className)} onClick={onClick}>
      <input className={styles.actionSelection} type="radio" checked={value} readOnly></input>
      <label>{title}</label>
    </div>
  )
}

Property.RadioButton = PropertyRadioButton;

const PropertyAccordion = ({ title, className, children }) => {
  return (
    <Accordion className={cx(styles.accordion, className)} flush >
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          <CustomAccordianHeader eventKey='0'>{title}</CustomAccordianHeader>
        </Accordion.Header>
        <Accordion.Body>
          {children}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  )
}

function CustomAccordianHeader({ children, eventKey, callback }) {
  const { activeEventKey } = React.useContext(AccordionContext);
  const isCurrentEventKey = activeEventKey === eventKey;

  return (
    <div className={styles.header}>
      {!isCurrentEventKey &&
        <FontAwesomeIcon icon={faPlus} />
      }

      {isCurrentEventKey &&
        <FontAwesomeIcon icon={faMinus} />
      }
      <span>{children}</span>
    </div>
  );
}

Property.Accordion = PropertyAccordion;

const PropertyBorderRadius = ({ value, onChange }) => {
  const lock = _.get(value, 'lock', false);
  const topLeft = _.get(value, 'topLeft', 0);
  const topRight = _.get(value, 'topRight', 0);
  const bottomRight = _.get(value, 'bottomRight', 0);
  const bottomLeft = _.get(value, 'bottomLeft', 0);

  const handleToggleLock = () => {
    var request = {
      ...value,
      lock: !lock
    }

    if (request.lock) {
      request = {
        ...request,
        topLeft: topLeft,
        topRight: topLeft,
        bottomRight: topLeft,
        bottomLeft: topLeft
      }
    }

    onChange(request);
  }


  const handleChange = (field, e) => {
    var input = parseInt(e.target.value) || 0;
    const min = e.target.min;
    const max = e.target.max;

    input = Math.max(Number(min), Math.min(Number(max), Number(input)));

    var request = {
      ...value,
      [field]: input
    }

    if (lock) {
      request = {
        ...request,
        topLeft: input,
        topRight: input,
        bottomRight: input,
        bottomLeft: input,
      }
    }

    onChange(request);
  }

  return (
    <div className={styles.borderRadius}>
      <button className={cx(styles.lock, {
        [styles.active]: lock
      })}
        onClick={handleToggleLock}
      >
        <span className="cleverstory-lock">
          <span className="path1"></span>
          <span className="path2"></span>
          <span className="path3"></span>
          <span className="path4"></span>
          <span className="path5"></span>
          <span className="path6"></span>
        </span>
      </button>

      <div className={styles.corner}>
        <input className={styles.value}
          type="text"
          pattern='[0-9]*'
          min={0}
          max={999}
          value={topLeft}
          onChange={handleChange.bind(this, 'topLeft')}
          onBlur={handleChange.bind(this, 'topLeft')}
          placeholder='0'
        />
        <label>TOP LEFT</label>
      </div>
      <div className={styles.corner}>
        <input className={styles.value}
          type="text"
          pattern='[0-9]*'
          min={0}
          max={999}
          value={topRight}
          onChange={handleChange.bind(this, 'topRight')}
          onBlur={handleChange.bind(this, 'topRight')}
          placeholder='0'
        />
        <label>TOP RIGHT</label>
      </div>
      <div className={styles.corner}>
        <input className={styles.value}
          type="text"
          pattern='[0-9]*'
          min={0}
          max={999}
          value={bottomRight}
          onChange={handleChange.bind(this, 'bottomRight')}
          onBlur={handleChange.bind(this, 'bottomRight')}
          placeholder='0'
        />
        <label>BOTTOM RIGHT</label>
      </div>
      <div className={styles.corner}>
        <input className={styles.value}
          type="text"
          pattern='[0-9]*'
          min={0}
          max={999}
          value={bottomLeft}
          onChange={handleChange.bind(this, 'bottomLeft')}
          onBlur={handleChange.bind(this, 'bottomLeft')}
          placeholder='0'
        />
        <label>BOTTOM LEFT</label>
      </div>
    </div>
  )
}

function propertyBorderRadiusEqual(prevProps, nextProps) {
  const paths = ['value', 'onChange', 'className'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.BorderRadius = React.memo(PropertyBorderRadius, propertyBorderRadiusEqual);

// DropShadow
const PropertyBoxShadow= ({ value, onChange }) => {
  const xValue = _.get(value, 'x', 0);
  const yValue = _.get(value, 'y', 0);
  const bValue = _.get(value, 'b', 0);

  const handleChange = (field, e) => {
    var input = parseInt(e.target.value) || 0;
    const min = e.target.min;
    const max = e.target.max;
    input = Math.max(Number(min), Math.min(Number(max), Number(input)));

    var request = {
      ...value,
      [field]: input
    }
    
    onChange(request);
  }

  return (
    <div className={styles.shadowInput}>
      <div className={styles.shadow}>
        <span>  X </span>
        <input className={styles.value}
          type="text"
          pattern="-?[0-9]*"
          min={-999}
          max={999}
          value={xValue}
          onChange={handleChange.bind(this, 'x')}
          placeholder='0'
        />
      </div>

      <div className={styles.shadow}>
      <span> Y </span>
        <input className={styles.value}
          type="text"
          pattern="-?[0-9]*"
          min={-999}
          max={999}
          value={yValue}
          onChange={handleChange.bind(this, 'y')}
          placeholder='0'
        />
      </div>

      <div className={styles.shadow}>
      <span> B </span>
        <input className={styles.value}
          type="text"
          pattern="-?[0-9]*"
          min={-999}
          max={999}
          value={bValue}
          onChange={handleChange.bind(this, 'b')}
          placeholder='0'
        />
      </div>
    </div>
  )
}

Property.BoxShadow = React.memo(PropertyBoxShadow);

//Property Switch
const PropertySwitch = ({ title, value, onChange, className }) => {
  return (
    <Form.Switch id={title} className={cx(styles.switch, className)}>
      <Form.Switch.Label onClick={onChange}>{title}</Form.Switch.Label>
      <Form.Switch.Input id={title} checked={value} isInvalid />
    </Form.Switch>
  );
};

function propertySwitchEqual(prevProps, nextProps) {
  const paths = ['title', 'value', 'onChange', 'className'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Property.Switch = React.memo(PropertySwitch, propertySwitchEqual);

const PropertyStretch = ({ id, display, title, onChange, ...rest }) => {
  return (
    <Property.Dialog className={styles.stretchDialog}
      width={230}
      target={`#${id}`}
      title={title || 'Stretch'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property className={styles.sizeProperty}>
          <div className={styles.sizeContainer}>
            <div className={cx(styles.size, styles.stretched, {
              [styles.active]: display === STRETCHED
            })} onClick={() => onChange(STRETCHED)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Screen</label>
            </div>
            <div className={cx(styles.size, {
              [styles.active]: display === PAGE
            })} onClick={() => onChange(PAGE)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Page</label>
            </div>
            <div className={cx(styles.size, styles.free, {
              [styles.active]: display === FREE
            })} onClick={() => onChange(FREE)}>
              <div className={styles.icon}>
                <div className={styles.inner}>
                  <div className={styles.pointer1} />
                  <div className={styles.pointer2} />
                  <div className={styles.pointer3} />
                  <div className={styles.pointer4} />
                </div>
              </div>
              <label>Free</label>
            </div>
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

Property.Stretch = React.memo(PropertyStretch);

export { Property };